import React, { memo, useMemo, useCallback, useState, useRef } from 'react';
import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import { EntityCrudService, MailingService } from 'services';
import Context from 'managers/Context';
import CONFIG from './TableConfig';
import NewEntityList from 'containers/components/NewEntityList';
import { getLiteral } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';
import { ensureRoute } from 'utils/routes';
import { publish } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';
import NoData from 'components/SvgIcons/emptyScreen/NoData';
import MarketingCampaigns from 'components/SvgIcons/emptyScreen/MarketingCampaigns';
import MarketingCampaignRow from './components/MarketingCampaignRow';
import DeleteModal from 'components/DeleteModal';
import CampaignPreviewTestModal from '../MarketingCampaignsForm/CampaignPreviewTestModal';
import DuplicateMarketingCampaignModal from './components/DuplicateMarketingCampaignModal';
import { parseCampaignSenderIdField } from '../utils/marketingSenderOptions';
import {
    normalizeMarketingCampaignStatusId,
    shouldOpenMarketingCampaignEditor,
} from '../utils/marketingCampaignStatus';
import { useSelector } from 'react-redux';
import { canManageMarketingCampaigns } from '../utils/marketingCampaignPermissions';

const Content = memo(() => {
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewPayload, setPreviewPayload] = useState(null);
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [duplicateCampaignId, setDuplicateCampaignId] = useState(null);
    const [isDuplicateSubmitting, setIsDuplicateSubmitting] = useState(false);
    const deleteModalRef = useRef(null);

    const canCreate = useSelector(canManageMarketingCampaigns);

    const tableParams = useMemo(() => {
        const newTableParams = {};

        return newTableParams;
    }, []);

    const handleOpenPreviewAndTest = useCallback(async (campaignId) => {
        if (!campaignId) {
            return;
        }
        try {
            const result = await EntityCrudService.get(CAMPAIGNS_MARKETING, campaignId);
            if (!result) {
                errorToast({ text: getLiteral('error_generalerror') });
                return;
            }
            const senderParsed = parseCampaignSenderIdField(result.senderId);
            const fromEmail = result.senderEmail || senderParsed.label || '';
            setPreviewPayload({
                from: fromEmail,
                subject: result.subject || '',
                body: result.body || '',
            });
            setPreviewModalOpen(true);
        } catch {
            errorToast({ text: getLiteral('error_generalerror') });
        }
    }, []);

    const handleClosePreviewModal = useCallback(() => {
        setPreviewModalOpen(false);
        setPreviewPayload(null);
    }, []);

    const handleOpenDetail = useCallback((campaign) => {
        if (!campaign?.id) {
            return;
        }
        const statusId = normalizeMarketingCampaignStatusId(campaign.status);
        if (shouldOpenMarketingCampaignEditor(statusId)) {
            ensureRoute(`${CAMPAIGNS_MARKETING.route}/edit/${campaign.id}`);
            return;
        }
        ensureRoute(`${CAMPAIGNS_MARKETING.route}/${campaign.id}`);
    }, []);

    const handleOpenDuplicateModal = useCallback((campaignId) => {
        if (!campaignId) {
            return;
        }
        setDuplicateCampaignId(campaignId);
        setDuplicateModalOpen(true);
    }, []);

    const handleCloseDuplicateModal = useCallback(() => {
        if (isDuplicateSubmitting) {
            return;
        }
        setDuplicateModalOpen(false);
        setDuplicateCampaignId(null);
    }, [isDuplicateSubmitting]);

    const handleConfirmDuplicate = useCallback(
        async (options) => {
            if (!duplicateCampaignId) {
                return;
            }
            setIsDuplicateSubmitting(true);
            try {
                await MailingService.duplicateCampaign(duplicateCampaignId, {
                    sender: options.copySender,
                    recipients: options.copyRecipients,
                    subject: options.copySubject,
                    design: options.copyDesign,
                });
                setDuplicateModalOpen(false);
                setDuplicateCampaignId(null);
                successToast({
                    title: getLiteral('title_toast_marketing_campaigns_duplicate_success'),
                    text: getLiteral('label_toast_marketing_campaigns_duplicate_success_message'),
                });
                ensureRoute(CAMPAIGNS_MARKETING.route);
                publish(`${REFRESH_TABLE}_${CAMPAIGNS_MARKETING.entity}`);
            } catch {
                errorToast({
                    title: getLiteral('title_toast_marketing_campaigns_duplicate_error'),
                    text: getLiteral('label_toast_marketing_campaigns_duplicate_error_message'),
                });
            } finally {
                setIsDuplicateSubmitting(false);
            }
        },
        [duplicateCampaignId],
    );

    const onDeleteModalRef = useCallback((ref) => {
        deleteModalRef.current = ref;
    }, []);

    const handleOpenDeleteCampaign = useCallback((campaignId) => {
        if (!campaignId) {
            return;
        }
        deleteModalRef.current?.open({ id: campaignId });
    }, []);

    const handleDeleteCampaign = useCallback(
        (id) =>
            EntityCrudService.del(CAMPAIGNS_MARKETING, id).then(() => {
                publish(`${REFRESH_TABLE}_${CAMPAIGNS_MARKETING.entity}`);
            }),
        [],
    );

    const loggedUserEmail = useMemo(
        () => Context.store.getState()?.config?.userData?.email || '',
        [],
    );

    const config = useMemo(() => {
        const base = CONFIG(tableParams);
        return {
            ...base,
            columnDefs: base.columnDefs.map((col) =>
                col.cellRenderer === 'marketingCampaignRow'
                    ? {
                          ...col,
                          cellRendererParams: {
                              ...col.cellRendererParams,
                              onPreviewAndTest: handleOpenPreviewAndTest,
                              onDuplicateCampaign: handleOpenDuplicateModal,
                              onDeleteCampaign: handleOpenDeleteCampaign,
                              onOpenDetail: handleOpenDetail,
                          },
                      }
                    : col,
            ),
        };
    }, [
        tableParams,
        handleOpenPreviewAndTest,
        handleOpenDuplicateModal,
        handleOpenDeleteCampaign,
        handleOpenDetail,
    ]);

    const emptyViewProps = useMemo(
        () => ({
            canCreate,
            icon: <NoData />,
            iconFirstTime: <MarketingCampaigns />,
            iconType: 'image',
            titleFirstTime: getLiteral('label_marketing_campaigns_reach_thousands_of_prospects'),
            subtitleFirstTime: getLiteral('label_marketing_campaigns_lets_start_first_campaign'),
            customButtonLiteral: 'action_marketing_campaigns_create_my_first_campaign',
        }),
        [canCreate],
    );

    const getRowNodeId = useCallback((data) => data.id, []);

    return (
        <div className="campaigns-table">
            {config && (
                <NewEntityList
                    id="fm-grid-campaigns-marketing"
                    entity={CAMPAIGNS_MARKETING}
                    config={config}
                    initOnReady={true}
                    useLazyLoad={true}
                    useCache={true}
                    useSort={true}
                    getRowNodeId={getRowNodeId}
                    emptyViewProps={emptyViewProps}
                    useFullWidthDivider={true}
                    components={{ marketingCampaignRow: MarketingCampaignRow }}
                    rowHeight={72}
                    loaderPlaceholderRowHeight={72}
                    forceRowUpdating={true}
                    discardCount={true}
                    domLayout="normal"
                />
            )}
            <CampaignPreviewTestModal
                isOpen={previewModalOpen}
                onRequestClose={handleClosePreviewModal}
                from={previewPayload?.from}
                subject={previewPayload?.subject}
                htmlPreview={previewPayload?.body}
                defaultRecipient={loggedUserEmail}
            />
            <DuplicateMarketingCampaignModal
                isOpen={duplicateModalOpen}
                onRequestClose={handleCloseDuplicateModal}
                onConfirm={handleConfirmDuplicate}
                isSubmitting={isDuplicateSubmitting}
            />
            <DeleteModal
                onRef={onDeleteModalRef}
                onDelete={handleDeleteCampaign}
                title={getLiteral('title_marketing_campaigns_delete_campaign_confirm_modal')}
                body={getLiteral('label_marketing_campaigns_delete_campaign_confirm_modal_message')}
                confirmText={getLiteral('action_delete')}
            />
        </div>
    );
});

export default Content;
